import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Button, Col, Collapse, Row, Form as AntdForm } from 'antd';
import { Form, Field } from 'react-final-form';
import { connect } from 'react-redux';

import { ClassificatorField, NumberField, YearSelect } from '../../atoms';
import AutoCompleteAccountsRegCodeField from '../../components/AutoCompleteAccountsField/AutoCompleteAccountsRegCodeField';
import { FormActions } from '../../components';

import { downloadFileByPostURL } from '../../utils/files';
import {
  FORM_TRADING_PLATFORM_AGREEMENTS_FILTER,
  tradingPlatformAgreementsFilter,
} from '../../constants/tradingPlatformAgreementsFilter';
import * as tradingPlatformAgreementsActionCreators from '../../actions/actionCreators/tradingPlatformAgreementsActionCreators';
import { links } from '../../utils';
import { fuelType, biofuelType } from '../../constants/classificators';
import { updateFormState } from '../../reducers/reactFinalFormReducer';
import FormStateToRedux from '../FormStateToRedux';

const { Panel } = Collapse;

const YearSelectField = ({ input, meta, label, placeholder }) => (
  <AntdForm.Item
    label={label}
    layout="vertical"
    validateStatus={meta.touched && meta.error ? 'error' : ''}
    help={meta.touched && meta.error}
  >
    <YearSelect
      value={input.value ?? null}
      onChange={(val) => input.onChange(val?.value ?? val)}
      onBlur={() => input.onBlur(input.value)}
      beforeCurrent={false}
      afterCurrent
      placeholder={placeholder}
      yearDistance={5}
    />
  </AntdForm.Item>
);

export const getAllowedFuelTypes = () => [
  fuelType.BIOMETHANE,
  fuelType.HYDROGEN,
  fuelType.RENEWABLE_ELECTRICITY,
  fuelType.RENEWABLE_ENERGY,
];

export const getAllowedBiofuelTypes = (fuelTypeVal) => {
  if (fuelTypeVal === undefined) {
    return [
      biofuelType.RFNBO_HYDROGEN,
      biofuelType.OTHER_RENEWABLE_HYDROGEN,
      biofuelType.ADVANCED,
      biofuelType.FIRST_GEN,
      biofuelType.OTHER,
    ];
  }
  if (fuelTypeVal === fuelType.HYDROGEN) {
    return [biofuelType.RFNBO_HYDROGEN, biofuelType.OTHER_RENEWABLE_HYDROGEN];
  }
  return [biofuelType.ADVANCED, biofuelType.FIRST_GEN, biofuelType.OTHER];
};

class TradingPlatformAgreementsFilter extends Component {
  static propTypes = {
    t: PropTypes.func.isRequired,
    searchTradingPlatformAgreements: PropTypes.func.isRequired,
    updateFormState: PropTypes.func.isRequired,
  };

  lastUsedFilters = {};

  handleCsvExport = () => {
    downloadFileByPostURL(
      links.tsAllAgreementsCsv,
      this.lastUsedFilters,
      'Eelkokkulepped.csv'
    );
  };

  onClear = (form) => {
    form.restart({});
    this.lastUsedFilters = {};
    this.props.updateFormState(FORM_TRADING_PLATFORM_AGREEMENTS_FILTER, {
      values: {},
    });
    this.props.searchTradingPlatformAgreements({});
  };

  onSubmit = async (values) => {
    const { searchTradingPlatformAgreements, updateFormState } = this.props;

    this.lastUsedFilters = values;
    updateFormState(FORM_TRADING_PLATFORM_AGREEMENTS_FILTER, { values });
    await searchTradingPlatformAgreements(values);
  };

  render() {
    const { t } = this.props;

    return (
      <>
        <Collapse>
          <Panel
            key="agreements-filter"
            header={t('agreements.filter.panelTitle')}
          >
            <Form
              initialValues={{}}
              onSubmit={this.onSubmit}
              render={({ handleSubmit, form }) => {
                const vals = form.getState().values || {};
                const currentFuelType =
                  vals[tradingPlatformAgreementsFilter.FUEL_TYPE];

                return (
                  <form onSubmit={handleSubmit}>
                    <FormStateToRedux
                      form={FORM_TRADING_PLATFORM_AGREEMENTS_FILTER}
                    />

                    <Row gutter={24}>
                      <Col span={8}>
                        <Field
                          name={tradingPlatformAgreementsFilter.STATUS}
                          label={t('agreements.filter.status')}
                          component={ClassificatorField}
                          classificatorType={ClassificatorField.types.tsStatus}
                          hasFeedback={false}
                        />
                      </Col>
                      <Col span={8}>
                        <Field
                          name={
                            tradingPlatformAgreementsFilter.SELLER_REGISTER_CODE
                          }
                          label={t('agreements.filter.seller')}
                          component={AutoCompleteAccountsRegCodeField}
                          hasFeedback={false}
                        />
                      </Col>
                      <Col span={8}>
                        <Field
                          name={
                            tradingPlatformAgreementsFilter.BUYER_REGISTER_CODE
                          }
                          label={t('agreements.filter.buyer')}
                          component={AutoCompleteAccountsRegCodeField}
                          hasFeedback={false}
                        />
                      </Col>
                    </Row>

                    <Row gutter={24}>
                      <Col span={8}>
                        <Field name={tradingPlatformAgreementsFilter.FUEL_TYPE}>
                          {({ input }) => (
                            <ClassificatorField
                              {...input}
                              label={t('agreements.filter.fuelType')}
                              classificatorType={
                                ClassificatorField.types.tsFuelType
                              }
                              hasFeedback={false}
                              allowed={getAllowedFuelTypes()}
                              onChange={(val, ...rest) => {
                                input.onChange(val, ...rest);
                                form.change(
                                  tradingPlatformAgreementsFilter.FUEL_GENERATION,
                                  undefined
                                );
                              }}
                            />
                          )}
                        </Field>
                      </Col>

                      {currentFuelType !== fuelType.RENEWABLE_ELECTRICITY && (
                        <Col span={8}>
                          <Field
                            name={
                              tradingPlatformAgreementsFilter.FUEL_GENERATION
                            }
                            label={t('agreements.filter.fuelGeneration')}
                            component={ClassificatorField}
                            classificatorType={
                              ClassificatorField.types.biofuelType
                            }
                            hasFeedback={false}
                            allowed={getAllowedBiofuelTypes(currentFuelType)}
                          />
                        </Col>
                      )}

                      <Col span={8}>
                        <Field
                          name={tradingPlatformAgreementsFilter.AGREEMENT_TYPE}
                          label={t('agreements.filter.agreementType')}
                          component={ClassificatorField}
                          classificatorType={
                            ClassificatorField.types.agreementType
                          }
                          hasFeedback={false}
                        />
                      </Col>
                    </Row>

                    <Row gutter={24}>
                      <Col span={8}>
                        <Field
                          name={
                            tradingPlatformAgreementsFilter.FINAL_CONSUMPTION_YEAR
                          }
                          component={YearSelectField}
                          label={t('agreements.filter.finalConsumptionYear')}
                          placeholder={t('agreements.filter.selectYear')}
                          parse={(v) =>
                            v == null || v === '' ? undefined : Number(v)
                          }
                          format={(v) => (v == null ? null : v)}
                        />
                      </Col>

                      <Col span={8}>
                        <Field
                          name={tradingPlatformAgreementsFilter.ID}
                          label={t('agreements.filter.agreementId')}
                          component={NumberField}
                          hasFeedback={false}
                          precision={0}
                          parse={(v) =>
                            v === '' || v == null ? undefined : Number(v)
                          }
                        />
                      </Col>
                    </Row>

                    <FormActions>
                      <Button onClick={() => this.onClear(form)}>
                        {t('transactions.filter.clearBtn')}
                      </Button>
                      <Button type="primary" htmlType="submit">
                        {t('transactions.filter.submitBtn')}
                      </Button>
                    </FormActions>
                  </form>
                );
              }}
            />
          </Panel>
        </Collapse>

        <FormActions>
          <Button onClick={this.handleCsvExport}>
            {t('agreements.filter.csvGeneralBtn')}
          </Button>
        </FormActions>
      </>
    );
  }
}

const mapDispatchToProps = {
  ...tradingPlatformAgreementsActionCreators,
  updateFormState,
};

export default connect(
  null,
  mapDispatchToProps
)(TradingPlatformAgreementsFilter);
